import type { AbstractControl, ValidationErrors } from '@angular/forms';
import {
  ADDRESS_VALIDATION_ERROR_KEYS,
  requiredTrimmedValidator,
} from './address-field.validators';

/** Strict masked North American phone format used by the UI input mask. */
export const PHONE_NUMBER_STRICT_MASKED_REGEX = /^\(\d{3}\) \d{3}-\d{4}$/;

/** Strict ten-digit phone format without separators. */
export const PHONE_NUMBER_STRICT_RAW_REGEX = /^\d{10}$/;

/** Permitted phone number characters: digits and common North American formatting separators. */
export const PHONE_NUMBER_PERMITTED_CHARACTERS_REGEX = /^[\d(),\- ]*$/;

/** Required digit count for North American phone numbers in Phase 1. */
export const PHONE_NUMBER_DIGIT_COUNT = 10;

/**
 * Strips non-digit characters from a phone number input.
 */
export function normalizePhoneDigits(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Returns whether the value contains only digits and permitted phone formatting characters.
 */
export function containsOnlyPermittedPhoneCharacters(value: string): boolean {
  return PHONE_NUMBER_PERMITTED_CHARACTERS_REGEX.test(value.trim());
}

/**
 * Validates strict US/CA Phase 1 phone numbers when country code is locked to +1.
 * Accepts only `(XXX) XXX-XXXX` or ten raw digits.
 */
export function isValidNorthAmericanPhoneNumber(value: string): boolean {
  const trimmed = value.trim();

  if (!containsOnlyPermittedPhoneCharacters(trimmed)) {
    return false;
  }

  if (normalizePhoneDigits(trimmed).length !== PHONE_NUMBER_DIGIT_COUNT) {
    return false;
  }

  return (
    PHONE_NUMBER_STRICT_MASKED_REGEX.test(trimmed) || PHONE_NUMBER_STRICT_RAW_REGEX.test(trimmed)
  );
}

/**
 * Returns phone validation errors for a non-blank value, checking numeric characters before format.
 */
function getPhoneValidationError(value: string): ValidationErrors | null {
  const trimmed = value.trim();

  if (!containsOnlyPermittedPhoneCharacters(trimmed)) {
    return { [ADDRESS_VALIDATION_ERROR_KEYS.phoneNonNumeric]: true };
  }

  return isValidNorthAmericanPhoneNumber(trimmed)
    ? null
    : { [ADDRESS_VALIDATION_ERROR_KEYS.phoneInvalid]: true };
}

/**
 * Required North American phone validator for address sections.
 */
export function northAmericanPhoneValidator(control: AbstractControl): ValidationErrors | null {
  const requiredError = requiredTrimmedValidator(control);

  if (requiredError) {
    return requiredError;
  }

  const value = typeof control.value === 'string' ? control.value : '';

  return getPhoneValidationError(value);
}

/**
 * Optional North American phone validator: allows an empty value but still enforces
 * the number format when the user has typed something. Used for Ship To when recipient
 * notifications are disabled and the phone number is not required.
 */
export function optionalNorthAmericanPhoneValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const value = typeof control.value === 'string' ? control.value.trim() : '';

  if (!value) {
    return null;
  }

  return getPhoneValidationError(value);
}
